import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { AppToastPayload, subscribeAppToast } from '../utils/appToastBus'

const TOAST_DURATION_MS = 4000
const TOAST_ANIMATION_MS = 320
const NAVBAR_ANCHOR_ID = 'app-navbar'

type ToastMessageParts = {
  title: string
  detail?: string
}

const getToastTop = (): number => {
  const navbar = document.getElementById(NAVBAR_ANCHOR_ID)
  if (navbar) {
    return navbar.getBoundingClientRect().bottom + 8
  }
  return 80
}

const splitToastMessage = (message: string): ToastMessageParts => {
  const splitIndex = message.indexOf('. ')
  if (splitIndex === -1) {
    return { title: message }
  }

  return {
    detail: message.slice(splitIndex + 2),
    title: message.slice(0, splitIndex + 1),
  }
}

export const AppToastHost: React.FC = () => {
  const [toast, setToast] = useState<AppToastPayload | null>(null)
  const [topOffset, setTopOffset] = useState(getToastTop)
  const [isVisible, setIsVisible] = useState(false)
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isClosingRef = useRef(false)
  const toastRef = useRef<AppToastPayload | null>(null)

  toastRef.current = toast

  const clearDismissTimer = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
  }

  const clearExitTimer = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
  }

  const finishDismiss = useCallback(() => {
    clearExitTimer()
    isClosingRef.current = false
    setIsVisible(false)
    setToast(null)
    toastRef.current = null
  }, [])

  const dismissToast = useCallback(() => {
    if (!toastRef.current || isClosingRef.current) {
      return
    }

    clearDismissTimer()
    isClosingRef.current = true
    setIsVisible(false)

    exitTimerRef.current = setTimeout(() => {
      finishDismiss()
    }, TOAST_ANIMATION_MS)
  }, [finishDismiss])

  const showToast = useCallback(
    (nextToast: AppToastPayload) => {
      clearDismissTimer()
      clearExitTimer()
      isClosingRef.current = false
      setTopOffset(getToastTop())
      setToast(nextToast)
      setIsVisible(false)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true)
        })
      })

      dismissTimerRef.current = setTimeout(() => {
        dismissTimerRef.current = null
        dismissToast()
      }, TOAST_DURATION_MS)
    },
    [dismissToast],
  )

  useEffect(() => {
    return subscribeAppToast((nextToast) => {
      if (!nextToast) {
        return
      }
      showToast(nextToast)
    })
  }, [showToast])

  useEffect(() => {
    return () => {
      clearDismissTimer()
      clearExitTimer()
    }
  }, [])

  if (!toast) {
    return null
  }

  const { title, detail } = splitToastMessage(toast.message)

  const toastNode = (
    <div
      className={`app-toast-banner app-toast-banner--${toast.variant}${isVisible ? ' app-toast-banner--visible' : ''}`}
      style={{ top: topOffset }}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="app-toast-banner__close"
        aria-label="Dismiss notification"
        onClick={dismissToast}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <p className="app-toast-banner__message">
        <span className="app-toast-banner__title">{title}</span>
        {detail ? <span className="app-toast-banner__detail"> {detail}</span> : null}
      </p>
    </div>
  )

  return ReactDOM.createPortal(toastNode, document.body)
}
